<template>
  <v-app>
    <v-container fluid class="fill-height pa-0" style="background: linear-gradient(135deg, #EFEBE9 0%, #D7CCC8 100%);">
      <v-row no-gutters class="fill-height" align="center" justify="center">
        <v-col cols="12" sm="10" md="8" lg="6" xl="5">
          <v-card class="elevation-12 rounded-xl" style="overflow: hidden;">
            <!-- Header -->
            <v-card-title class="primary white--text pa-6 d-flex flex-column align-center justify-center text-center">
              <v-icon dark size="48" class="mb-3">mdi-account-edit</v-icon>
              <h2 class="font-weight-light mb-1">Completar Perfil</h2>
              <p class="mb-0 body-2">Por favor complete sus datos para continuar</p>
            </v-card-title>

            <v-card-text class="pa-6">
              <v-alert
                v-if="errorMessage"
                type="error"
                dismissible
                class="mb-4"
                @input="errorMessage = ''"
              >
                {{ errorMessage }}
              </v-alert>

              <v-alert
                type="info"
                outlined
                class="mb-4"
              >
                <v-icon left>mdi-information</v-icon>
                Es la primera vez que inicia sesión. Por favor complete sus datos personales.
              </v-alert>

              <form @submit.prevent="submitForm">
                <!-- DNI -->
                <v-text-field
                  v-model="formData.dni"
                  label="DNI / Documento de Identidad *"
                  prepend-inner-icon="mdi-card-account-details"
                  outlined
                  dense
                  class="mb-2"
                  :error-messages="$v.formData.dni.$error ? 'El DNI es requerido' : ''"
                  @blur="$v.formData.dni.$touch()"
                ></v-text-field>

                <!-- Nombre -->
                <v-text-field
                  v-model="formData.name"
                  label="Nombre *"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  class="mb-2"
                  :error-messages="$v.formData.name.$error ? 'El nombre es requerido' : ''"
                  @blur="$v.formData.name.$touch()"
                ></v-text-field>

                <!-- Apellido -->
                <v-text-field
                  v-model="formData.lastname"
                  label="Apellido *"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  class="mb-2"
                  :error-messages="$v.formData.lastname.$error ? 'El apellido es requerido' : ''"
                  @blur="$v.formData.lastname.$touch()"
                ></v-text-field>

                <!-- Fecha de Nacimiento -->
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedDate"
                      label="Fecha de Nacimiento *"
                      prepend-inner-icon="mdi-calendar"
                      outlined
                      dense
                      readonly
                      class="mb-2"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="$v.formData.dob.$error ? 'La fecha de nacimiento es requerida' : ''"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.dob"
                    @input="dateMenu = false"
                    :max="maxDate"
                    locale="es"
                  ></v-date-picker>
                </v-menu>

                <!-- Género -->
                <v-select
                  v-model="formData.gender"
                  :items="genderOptions"
                  label="Género *"
                  prepend-inner-icon="mdi-gender-male-female"
                  outlined
                  dense
                  class="mb-2"
                  :error-messages="$v.formData.gender.$error ? 'El género es requerido' : ''"
                  @blur="$v.formData.gender.$touch()"
                ></v-select>

                <!-- Teléfono -->
                <v-text-field
                  v-model="formData.phone"
                  label="Teléfono (opcional)"
                  prepend-inner-icon="mdi-phone"
                  outlined
                  dense
                  class="mb-2"
                ></v-text-field>

                <!-- Obra Social -->
                <v-select
                  v-model="formData.insurance_id"
                  :items="obrasSociales"
                  item-text="name"
                  item-value="value"
                  label="Obra Social (opcional)"
                  prepend-inner-icon="mdi-hospital-building"
                  outlined
                  dense
                  clearable
                  class="mb-4"
                  :loading="loadingObrasSociales"
                  no-data-text="No hay obras sociales disponibles"
                ></v-select>

                <v-btn
                  type="submit"
                  color="primary"
                  large
                  block
                  class="mb-4 rounded-lg"
                  :loading="saving"
                  :disabled="$v.$invalid || saving"
                >
                  <v-icon left>mdi-check</v-icon>
                  Guardar y Continuar
                </v-btn>
              </form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading Dialog -->
      <v-dialog
        v-model="saving"
        persistent
        width="300"
      >
        <v-card class="rounded-lg">
          <v-card-text class="text-center pa-6">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="mb-4"
            ></v-progress-circular>
            <p class="body-1 mb-0">Guardando perfil...</p>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { createPersonInMonolito, getObrasSociales } from '@/services/monolito'

export default {
  name: 'CompletarPerfil',
  mixins: [validationMixin],
  data() {
    return {
      formData: {
        dni: '',
        name: '',
        lastname: '',
        dob: '',
        gender: '',
        phone: '',
        insurance_id: null
      },
      genderOptions: [
        { text: 'Masculino', value: 'm' },
        { text: 'Femenino', value: 'f' }
      ],
      obrasSociales: [],
      loadingObrasSociales: false,
      dateMenu: false,
      saving: false,
      errorMessage: ''
    }
  },
  validations: {
    formData: {
      dni: { required },
      name: { required },
      lastname: { required },
      dob: { required },
      gender: { required }
    }
  },
  computed: {
    formattedDate() {
      if (!this.formData.dob) return ''
      const [year, month, day] = this.formData.dob.split('-')
      return day + '/' + month + '/' + year
    },
    maxDate() {
      // Fecha máxima: hoy (no se puede nacer en el futuro)
      return new Date().toISOString().substr(0, 10)
    },
    userEmail() {
      // Obtener email del store o localStorage
      const profile = this.$store.state.user_profile
      if (profile && profile.email) {
        return profile.email
      }
      // Intentar del payload
      const payload = this.$store.state.user_payload
      if (payload && payload.email) {
        return payload.email
      }
      // Intentar de localStorage
      const storedProfile = localStorage.getItem('user_profile')
      if (storedProfile) {
        try {
          const parsed = JSON.parse(storedProfile)
          return parsed.email
        } catch (e) {
          console.warn('Error parsing stored profile')
        }
      }
      return null
    }
  },
  async created() {
    await this.loadObrasSociales()
    this.prefillFromSignup()
  },
  methods: {
    prefillFromSignup() {
      // Primero intentar cargar datos guardados del signup
      const signupData = localStorage.getItem('signup_partial_profile')
      if (signupData) {
        try {
          const parsed = JSON.parse(signupData)
          // ...existing code...
          
          if (parsed.name) this.formData.name = parsed.name
          if (parsed.lastname) this.formData.lastname = parsed.lastname
          if (parsed.dni) this.formData.dni = parsed.dni
          if (parsed.gender) this.formData.gender = parsed.gender
          if (parsed.dob) this.formData.dob = parsed.dob
          if (parsed.phone) this.formData.phone = parsed.phone
          if (parsed.insurance_id) this.formData.insurance_id = parsed.insurance_id
          
          // Limpiar los datos del signup después de usarlos
          localStorage.removeItem('signup_partial_profile')
          return
        } catch (e) {
          console.warn('Error parseando datos del signup:', e)
        }
      }
      
      // Si no hay datos del signup, intentar del perfil
      this.prefillFromProfile()
    },
    prefillFromProfile() {
      // Pre-llenar con datos del perfil si existen
      const profile = this.$store.state.user_profile
      if (profile) {
        if (profile.name) this.formData.name = profile.name
        if (profile.email) {
          // Si el username parece un nombre, usarlo
          const username = profile.username || ''
          if (username && !username.includes('@')) {
            const parts = username.split(' ')
            if (parts.length >= 2) {
              this.formData.name = parts[0]
              this.formData.lastname = parts.slice(1).join(' ')
            } else {
              this.formData.name = username
            }
          }
        }
      }
    },
    async loadObrasSociales() {
      this.loadingObrasSociales = true
      try {
        const data = await getObrasSociales()
        this.obrasSociales = data || []
      } catch (error) {
        console.error('Error cargando obras sociales:', error)
        this.obrasSociales = []
      } finally {
        this.loadingObrasSociales = false
      }
    },
    async submitForm() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      if (!this.userEmail) {
        this.errorMessage = 'No se pudo obtener el email del usuario. Por favor vuelva a iniciar sesión.'
        return
      }

      this.saving = true
      this.errorMessage = ''

      try {
        // Crear el perfil en el monolito
        const personData = {
          dni: this.formData.dni,
          name: this.formData.name,
          lastname: this.formData.lastname,
          dob: this.formData.dob,
          gender: this.formData.gender,
          phone: this.formData.phone || null,
          insurance_id: this.formData.insurance_id || null
        }

        // ...existing code...
        
        await createPersonInMonolito(this.userEmail, personData)
        
        // ...existing code...
        
        // Actualizar el store con los datos del paciente
        this.$store.commit('set_user_profile', {
          ...this.$store.state.user_profile,
          ...personData,
          perfilCompleto: true
        })

        // Redirigir al usuario según su rol
        this.redirectAfterComplete()
        
      } catch (error) {
        console.error('Error guardando perfil:', error)
        
        if (error.response) {
          const status = error.response.status
          const data = error.response.data
          
          if (status === 409) {
            this.errorMessage = 'Ya existe un paciente con este DNI. Contacte a soporte.'
          } else if (status === 400) {
            this.errorMessage = data.message || 'Datos inválidos. Por favor revise el formulario.'
          } else if (status === 401) {
            this.errorMessage = 'Sesión expirada. Por favor vuelva a iniciar sesión.'
            setTimeout(() => {
              this.$router.push('/login')
            }, 2000)
          } else {
            this.errorMessage = 'Error del servidor. Por favor intente nuevamente.'
          }
        } else {
          this.errorMessage = 'Error de conexión. Por favor verifique su conexión a internet.'
        }
      } finally {
        this.saving = false
      }
    },
    redirectAfterComplete() {
      // Después de completar el perfil, redirigir a /cuenta
      // ...existing code...
      this.$router.push('/editaccount')
    }
  }
}
</script>

<style scoped>
.v-text-field {
  margin-bottom: 8px;
}
</style>
